import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output, QueryList, ViewChild,
  ViewChildren
} from '@angular/core';
import { LMListCardService } from '@list/services/lm-list-card.service';
import { LMSpecService } from '@services/lm-spec.service';
import { LMUrlUtilService } from '@services/utils';
import { LMComponentInfoService } from '@services/lm-componentinfo.service';
import { SohoRatingComponent } from 'ids-enterprise-ng';
import { LMNavigationService } from '@app/components';
import { LMCardBaseDirective } from '@card/lm-card-base.directive';

@Component({
  selector: 'lm-list-card',
  templateUrl: './lm-list-card.component.html',
  styleUrls: [ './lm-list-card.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class LMListCardComponent extends LMCardBaseDirective implements OnInit, AfterViewInit {

  @ViewChild('FlexContainer') flexContainer: ElementRef;
  @ViewChildren('CardColumn') cardColumns: QueryList<ElementRef>;
  @ViewChildren(SohoRatingComponent) ratings;
  @HostListener('window:resize') onResize() {
    // LMWEB-2465
    // Timeout to allow DOM reflow to finish before check
    setTimeout(() => this.checkRightColumn(), 250);
  }

  @Input() listUrlInfo: LMListUrlInfo;
  @Input() listSpec: LMListSpec;
  @Input() dataView: LMDataView;
  @Input() boldFirstRow = true;

  @Output() uiLinkEvent = new EventEmitter<LMUILinkEvent>();

  public cardTemplateData: LMCardTemplateData;
  public rightColumnIsWrapped = false;
  public noDataAvailable: string;

  constructor(
    protected lmComponentInfoService: LMComponentInfoService,
    protected lmNavigationService: LMNavigationService,
    protected lmUrlUtilService: LMUrlUtilService,
    private lmListCardService: LMListCardService,
    private lmSpecService: LMSpecService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super(lmComponentInfoService, lmNavigationService, lmUrlUtilService);
  }

  ngOnInit() {
    let displayFields: LMListDisplayField = this.listSpec.busListSpec.displayFields[0];

    if (this.listSpec.busListSpec.responsiveCardView) {
      displayFields = this.listSpec.busListSpec.responsiveCardView;
    }

    this.cardTemplateData = this.lmListCardService.getCardTemplateData(displayFields, this.dataView);
    this.noDataAvailable = Soho.Locale.translate('NoDataAvailable');
  }

  ngAfterViewInit() {
    this.checkRightColumn();

    setTimeout(() => {
      this.changeDetectorRef.markForCheck();

      if (this.ratings) {
        // ensure all rating components are disabled
        this.ratings.forEach((rating: SohoRatingComponent) => {
          rating.readonly();
        });
      }
    }, 1);
  }

  public navigate(item: LMListViewTemplateField) {
    const uiLinkEvent: LMUILinkEvent = {
      columnDef: null,
      dataView: item.navigationParams.dataView,
      event,
      navigationParams: item.navigationParams };
    this.uiLinkEvent.emit(uiLinkEvent);
  }

  public isBlankLine(item: LMListViewTemplateField) {
    if (item === undefined) {
      return false;
    }
    return item.type  === 'BLANKLINE';
  }

  public isAlert(item: LMListViewTemplateField) {
    if (item === undefined || item.alertCondition === undefined) {
      return false;
    }

    return item.alertCondition !== undefined;
  }

  public displayFieldAsTitle(index: number, columns: LMCardTemplateColumn): boolean {

    if (columns.column === undefined) {
      return false;
    }

    return index === 0 && columns.column.length > 1;
  }

  public alertClass(item: LMListViewTemplateField): string {
    return 'icon-' + item.alertCondition.icon;
  }

  private checkRightColumn(): void {
    let mainColumnHeight = 0;

    if (this.cardColumns === undefined || this.cardColumns.length === 0) {
      return;
    }

    if (this.cardColumns.length === 2) {
      this.cardColumns?.forEach((elementRef, index) => {
        if (index === 0) {
          mainColumnHeight = elementRef?.nativeElement?.clientHeight;
        }

        if (index === 1) {
          if (this?.flexContainer?.nativeElement?.clientHeight > mainColumnHeight) {
            elementRef.nativeElement.classList.add('lm-card-column-wrapped');
          } else {
            elementRef?.nativeElement?.classList.remove('lm-card-column-wrapped');
          }
        }
      });
    }
  }

  public getComponentInfo(additionalInfo?: LMCompInfoAdditionalInfo): LMComponentInfo {
    let cardviewSpec: LMCardviewSpec = {};
    if (this.listSpec.busListSpec.displayFields && this.listSpec.busListSpec.displayFields[0].type === 'CardView') {
      cardviewSpec = this.listSpec.busListSpec.displayFields[ 0 ];

      const compConfigInfo: ConfigInfo = {};
      compConfigInfo['personalization'] = cardviewSpec ? cardviewSpec.personalization : undefined;
      compConfigInfo['configuration'] = cardviewSpec ? cardviewSpec.configuration : undefined;
      compConfigInfo['configStamp'] = cardviewSpec ? cardviewSpec.configStamp : 0;
      compConfigInfo['classConfigStamp'] = cardviewSpec ? cardviewSpec.classConfigStamp : 0;

      if (cardviewSpec.type === 'CardView') {
        cardviewSpec['componentType'] =  'CARDVIEW';
      }
      cardviewSpec['configInfo'] = compConfigInfo;

      const formInfo: LMComponentInfo = {
        componentType: cardviewSpec.componentType,
        name: cardviewSpec ? cardviewSpec.id : undefined,
        cardviewSpec,
        hasConfigAccess: false,
        type: 'card',
      };

      return formInfo;
    }
  }
}
